import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/Toast';
import { formatApiError } from '../utils/errors';
import { companyApi } from '../api/company';
import { taxRatesApi } from '../api/taxRates';
import { documentSerialsApi } from '../api/documentSerials';
import type { TaxRateType } from '../types/taxRate';
import {
  getWorkspaceId,
} from '../api/workspaces';
import { formatDate } from '../utils/formatDate';
import type { CompanyDto, CreateCompanyRequest, UpdateCompanyRequest } from '../types/company';
import LoadingSpinner from '../components/LoadingSpinner';
import { useReportScreen } from '../components/ScreenContext';
import { aiAuthorityApi, type AuthorityConfigDto } from '../api/aiAuthority';
import { isAdmin } from '../api/client';

const TAX_TYPE_LABELS: Record<string, string> = {
  'Standard': 'Standartinis',
  'Reduced': 'Sumažintas',
  'SuperReduced': 'Ypatingai sumažintas',
  'Zero': 'Nulinis',
  'Exempt': 'Neapmokestinamas',
  'ReverseCharge': 'Atvirkštinis apmokestinimas',
};

export default function Settings() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const hasWorkspace = !!getWorkspaceId();
  const admin = isAdmin();
  const [showTaxForm, setShowTaxForm] = useState(false);
  const [savingTax, setSavingTax] = useState(false);
  const [newTax, setNewTax] = useState<{
    name: string; rate: number; type: TaxRateType;
    vatAccountDebit: number; vatAccountCredit: number; vatAccountPurchase: number;
    dateFrom: string; dateTo: string | null;
  }>({ name: '', rate: 0, type: 'Standard', vatAccountDebit: 0, vatAccountCredit: 0, vatAccountPurchase: 0, dateFrom: new Date().toISOString().slice(0, 10), dateTo: null });

  const { data: company, isLoading: companyLoading } = useQuery({
    queryKey: ['company'],
    queryFn: companyApi.get,
    enabled: hasWorkspace,
  });

  const { data: taxRates } = useQuery({
    queryKey: ['tax-rates'],
    queryFn: taxRatesApi.list,
    enabled: hasWorkspace,
  });

  const { data: serials } = useQuery({
    queryKey: ['document-serials'],
    queryFn: documentSerialsApi.list,
    enabled: hasWorkspace,
  });

  // AI screen context (actions are auto-discovered from semantic HTML)
  const serialCount = serials?.length ?? 0;
  useReportScreen(`Nustatymai (${serialCount} seriju)`, {
    recordCount: serialCount,
  });

  const updateCompanyMutation = useMutation({
    mutationFn: companyApi.update,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company'] }),
    onError: (err: Error) => addToast(formatApiError(err), 'error'),
  });

  const createCompanyMutation = useMutation({
    mutationFn: companyApi.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company'] }),
    onError: (err: Error) => addToast(formatApiError(err), 'error'),
  });

  if (companyLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-xl font-bold text-gray-900">{t('settings.title')}</h1>

      {/* Company info */}
      <CompanySection company={company} onSave={(data) => updateCompanyMutation.mutate(data)} onCreate={(data) => createCompanyMutation.mutate(data)} saving={updateCompanyMutation.isPending || createCompanyMutation.isPending} />

      {/* Tax rates */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-semibold text-gray-900">{t('settings.taxRates')}</h2>
          <button
            className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 border border-primary-200"
            onClick={() => setShowTaxForm(v => !v)}
          >
            {showTaxForm ? t('common.cancel') : t('settings.addTaxRate')}
          </button>
        </div>
        {showTaxForm && (
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('common.name')} *</label>
                <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={newTax.name} onChange={e => setNewTax(tx => ({ ...tx, name: e.target.value }))} placeholder="pvz. PVM 21%" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('settings.rate')} *</label>
                <input type="number" step="0.01" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={newTax.rate || ''} onChange={e => setNewTax(tx => ({ ...tx, rate: parseFloat(e.target.value) / 100 || 0 }))} placeholder="21" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('common.type')}</label>
                <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={newTax.type} onChange={e => setNewTax(tx => ({ ...tx, type: e.target.value as any }))}>
                  <option value="Standard">{TAX_TYPE_LABELS['Standard']}</option>
                  <option value="Reduced">{TAX_TYPE_LABELS['Reduced']}</option>
                  <option value="SuperReduced">{TAX_TYPE_LABELS['SuperReduced']}</option>
                  <option value="Zero">{TAX_TYPE_LABELS['Zero']}</option>
                  <option value="Exempt">{TAX_TYPE_LABELS['Exempt']}</option>
                  <option value="ReverseCharge">{TAX_TYPE_LABELS['ReverseCharge']}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('settings.validFrom')}</label>
                <input type="date" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={newTax.dateFrom} onChange={e => setNewTax(tx => ({ ...tx, dateFrom: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('settings.vatDebitAccount')}</label>
                <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={newTax.vatAccountDebit || ''} onChange={e => setNewTax(tx => ({ ...tx, vatAccountDebit: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('settings.vatCreditAccount')}</label>
                <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={newTax.vatAccountCredit || ''} onChange={e => setNewTax(tx => ({ ...tx, vatAccountCredit: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('settings.vatPurchaseAccount')}</label>
                <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={newTax.vatAccountPurchase || ''} onChange={e => setNewTax(tx => ({ ...tx, vatAccountPurchase: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="flex items-end">
                <button className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                  disabled={!newTax.name || newTax.rate <= 0 || savingTax}
                  onClick={async () => {
                    setSavingTax(true);
                    try {
                      await taxRatesApi.create(newTax);
                      queryClient.invalidateQueries({ queryKey: ['tax-rates'] });
                      setShowTaxForm(false);
                      setNewTax({ name: '', rate: 0, type: 'Standard', vatAccountDebit: 0, vatAccountCredit: 0, vatAccountPurchase: 0, dateFrom: new Date().toISOString().slice(0, 10), dateTo: null });
                    } catch (err) { alert(err instanceof Error ? err.message : 'Nepavyko sukurti mokesčio tarifo'); }
                    setSavingTax(false);
                  }}>
                  {savingTax ? t('common.saving') : t('common.create')}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th className="text-left">{t('common.name')}</th>
                <th className="text-right">{t('settings.rate')}</th>
                <th className="text-left">{t('common.type')}</th>
                <th className="text-right">{t('settings.vatDebitAccount')}</th>
                <th className="text-right">{t('settings.vatCreditAccount')}</th>
                <th className="text-left">{t('settings.validFrom')}</th>
                <th className="text-left">{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {taxRates?.map((rate) => (
                <tr key={rate.id} className="border-b border-gray-50">
                  <td>{rate.name}</td>
                  <td className="num">{(rate.rate * 100).toFixed(2)}%</td>
                  <td>{TAX_TYPE_LABELS[rate.type] ?? rate.type}</td>
                  <td className="num">{rate.vatAccountDebit}</td>
                  <td className="num">{rate.vatAccountCredit}</td>
                  <td>{formatDate(rate.dateFrom)}</td>
                  <td>
                    <span className={`text-xs font-medium ${rate.isObsolete ? 'text-gray-400' : 'text-green-600'}`}>
                      {rate.isObsolete ? t('common.obsolete') : t('common.active')}
                    </span>
                  </td>
                </tr>
              ))}
              {(!taxRates || taxRates.length === 0) && (
                <tr><td colSpan={7} className="text-center text-gray-400 py-4">{t('settings.noTaxRates')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Document serials */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-semibold text-gray-900">{t('settings.documentSerials')}</h2>
          <button
            className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 border border-primary-200"
            onClick={() => navigate('/settings/document-serials')}
          >
            {t('settings.manageSerials')}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th className="text-left">{t('common.type')}</th>
                <th className="text-left">{t('common.code')}</th>
                <th className="text-right">{t('settings.lastNumber')}</th>
                <th className="text-left">{t('settings.pattern')}</th>
                <th className="text-left">{t('settings.reset')}</th>
                <th className="text-left">{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {serials?.map((serial) => (
                <tr key={serial.id} className="border-b border-gray-50">
                  <td>{serial.serialType}</td>
                  <td className="font-mono">{serial.serialCode}</td>
                  <td className="num">{serial.lastNumber}</td>
                  <td className="font-mono text-xs text-gray-500">{serial.pattern || '(paprastas)'}</td>
                  <td>{serial.resetFrequency}</td>
                  <td>
                    <span className={`text-xs font-medium ${serial.isObsolete ? 'text-gray-400' : 'text-green-600'}`}>
                      {serial.isObsolete ? t('common.obsolete') : t('common.active')}
                    </span>
                  </td>
                </tr>
              ))}
              {(!serials || serials.length === 0) && (
                <tr><td colSpan={6} className="text-center text-gray-400 py-4">{t('settings.noDocumentSerials')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Agent Settings — admin-only */}
      {admin && <AIAgentSettingsSection />}

      {/* Settings sub-pages */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">{t('settings.administration')}</h2>
        </div>
        <div className="divide-y divide-gray-100">
          <button className="w-full text-left px-4 py-3 hover:bg-gray-50 flex justify-between items-center"
            onClick={() => navigate('/settings/document-serials')}>
            <div>
              <div className="text-sm font-medium text-gray-900">{t('settings.documentSerials')}</div>
              <div className="text-xs text-gray-500">{t('settings.docSerialsDescription')}</div>
            </div>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {admin && (
            <button className="w-full text-left px-4 py-3 hover:bg-gray-50 flex justify-between items-center"
              onClick={() => navigate('/settings/roles')}>
              <div>
                <div className="text-sm font-medium text-gray-900">{t('settings.userRolesPermissions')}</div>
                <div className="text-xs text-gray-500">{t('settings.rolesDescription')}</div>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          {admin && (
            <button className="w-full text-left px-4 py-3 hover:bg-gray-50 flex justify-between items-center"
              onClick={() => navigate('/settings/backup')}>
              <div>
                <div className="text-sm font-medium text-gray-900">{t('settings.dataBackup')}</div>
                <div className="text-xs text-gray-500">{t('settings.backupDescription')}</div>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          {admin && (
            <button className="w-full text-left px-4 py-3 hover:bg-gray-50 flex justify-between items-center"
              onClick={() => navigate('/settings/email')}>
              <div>
                <div className="text-sm font-medium text-gray-900">{t('settings.emailConfiguration')}</div>
                <div className="text-xs text-gray-500">{t('settings.emailDescription')}</div>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CompanySection({ company, onSave, onCreate, saving }: { company: CompanyDto | undefined; onSave: (data: UpdateCompanyRequest) => void; onCreate: (data: CreateCompanyRequest) => void; saving: boolean }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<UpdateCompanyRequest>({
    code: company?.code ?? 0,
    name: company?.name ?? '',
    vatCode: company?.vatCode ?? '',
    address: '',
    baseCurrency: company?.baseCurrency ?? 'EUR',
    headPerson: company?.headPerson ?? '',
    email: '',
    bankAccount: '',
  });

  const handleSave = () => {
    onSave(form);
    setEditing(false);
  };

  if (!company) {
    return <CreateCompanyForm onCreate={onCreate} saving={saving} />;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="font-semibold text-gray-900">{t('settings.companyInfo')}</h2>
        <button
          className="text-xs text-primary-600 hover:underline"
          onClick={() => setEditing(!editing)}
        >
          {editing ? t('common.cancel') : t('common.edit')}
        </button>
      </div>

      {editing ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.code')}</label>
            <input
              type="number"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.name')}</label>
            <input
              type="text"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('settings.vatCode')}</label>
            <input
              type="text"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              value={form.vatCode}
              onChange={(e) => setForm({ ...form, vatCode: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('settings.address')}</label>
            <input
              type="text"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('settings.headPerson')}</label>
            <input
              type="text"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              value={form.headPerson}
              onChange={(e) => setForm({ ...form, headPerson: e.target.value })}
            />
          </div>
          <div className="flex items-end">
            <button
              className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-gray-500 text-xs">{t('common.code')}</div>
            <div className="font-medium">{company.code}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">{t('common.name')}</div>
            <div className="font-medium">{company.name}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">{t('settings.vatCode')}</div>
            <div className="font-medium">{company.vatCode}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">{t('common.currency')}</div>
            <div className="font-medium">{company.baseCurrency}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">{t('settings.headPerson')}</div>
            <div className="font-medium">{company.headPerson}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateCompanyForm({ onCreate, saving }: { onCreate: (data: CreateCompanyRequest) => void; saving: boolean }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateCompanyRequest>({
    code: 0,
    name: '',
    vatCode: '',
    address: '',
    baseCurrency: 'EUR',
    headPerson: '',
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900 mb-3">{t('settings.createCompany')}</h2>
      <p className="text-sm text-gray-500 mb-4">{t('settings.createCompanyDescription')}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('settings.companyCode')} *</label>
          <input
            type="number"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.code || ''}
            onChange={(e) => setForm({ ...form, code: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('common.name')} *</label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('settings.vatCode')}</label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.vatCode}
            onChange={(e) => setForm({ ...form, vatCode: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('settings.address')}</label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('settings.headPerson')}</label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.headPerson}
            onChange={(e) => setForm({ ...form, headPerson: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('common.currency')}</label>
          <input
            type="text"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.baseCurrency ?? 'EUR'}
            onChange={(e) => setForm({ ...form, baseCurrency: e.target.value })}
          />
        </div>
        <div className="flex items-end">
          <button
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            onClick={() => onCreate(form)}
            disabled={saving || !form.name || !form.code}
          >
            {saving ? 'Kuriama…' : 'Sukurti įmonę'}
          </button>
        </div>
      </div>
    </div>
  );
}

// AI Agent Settings Section
function AIAgentSettingsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const hasWorkspace = !!getWorkspaceId();

  const { data: configs, isLoading } = useQuery<AuthorityConfigDto[]>({
    queryKey: ['ai-authority-configs'],
    queryFn: aiAuthorityApi.getConfigs,
    staleTime: 60_000,
    enabled: hasWorkspace,
  });

  const [localConfigs, setLocalConfigs] = useState<Record<string, string>>({});

  const handleChange = (workflowName: string, authorityLevel: string) => {
    setLocalConfigs(prev => ({ ...prev, [workflowName]: authorityLevel }));
  };

  const handleSave = async () => {
    for (const [workflow, level] of Object.entries(localConfigs)) {
      await aiAuthorityApi.updateConfig(workflow, level);
    }
    setLocalConfigs({});
    queryClient.invalidateQueries({ queryKey: ['ai-authority-configs'] });
  };

  const authorityOptions = [
    { value: 'Observe', label: 'Stebėti', color: 'bg-gray-100 text-gray-700' },
    { value: 'Suggest', label: 'Pasiūlyti', color: 'bg-primary-100 text-primary-700' },
    { value: 'ActWithReview', label: 'Veikti su patvirtinimu', color: 'bg-amber-100 text-amber-700' },
    { value: 'ActAutonomous', label: 'Veikti savarankiškai', color: 'bg-green-100 text-green-700' },
  ];

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h2 className="font-semibold text-gray-900">{t('settings.aiAgentSettings')}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{t('settings.aiAgentDescription')}</p>
        </div>
        {Object.keys(localConfigs).length > 0 && (
          <button
            className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 border border-primary-200"
            onClick={handleSave}
          >
            {t('settings.saveChanges', { count: Object.keys(localConfigs).length })}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="dense w-full">
          <thead>
            <tr>
              <th className="text-left">{t('settings.workflow')}</th>
              <th className="text-left">{t('settings.authorityLevel')}</th>
              <th className="text-left">{t('settings.aiDescription')}</th>
            </tr>
          </thead>
          <tbody>
            {configs?.map((config) => {
              const currentLevel = localConfigs[config.workflowName] ?? config.authorityLevel;
              return (
                <tr key={config.workflowName}>
                  <td className="font-medium text-sm">{config.workflowName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                  <td>
                    <select
                      value={currentLevel}
                      onChange={(e) => handleChange(config.workflowName, e.target.value)}
                      className={`border border-gray-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                        authorityOptions.find(o => o.value === currentLevel)?.color ?? ''
                      }`}
                    >
                      {authorityOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="text-xs text-gray-500">{config.description}</td>
                </tr>
              );
            })}
            {(!configs || configs.length === 0) && (
              <tr><td colSpan={3} className="text-center text-gray-400 py-4">{t('settings.noAiWorkflows')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
        Pakeitimai registruojami audito žurnale. Numatytasis lygis — „Pasiūlyti“, kol aiškiai nepakeistas.
      </div>
    </div>
  );
}
